import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { isEmpty, size } from 'lodash';
import { ShowMore } from '@ebsco-ui/ebsco-ui';

import {
  ClassificationType,
  identifierType,
  SearchInstanceDto,
  ShowMoreButton,
} from '@app/components';
import {
  useInstanceDetailsRoutePath,
  useInstanceRelationships,
} from '@app/hooks';
import { formatArrayToString } from '@app/utils';
import {
  additionalInformationNoteTypeMessages,
  contentsSummaryNoteTypeMessages,
  langMessages,
  publicationDetailsNoteTypeMessages,
  sharedMessages,
} from '@app/translations';

import {
  formatClassifications,
  formatElectronicAccess,
  formatInstanceMetadata,
  formatNotes,
  formatSiblingInstancesList,
  getExternalLink,
  getNotesData,
  getPublishers,
  renderMetadataList,
} from './utils';

const childInstancesLimit = 5;

export const useInstanceSections = ({
  instance,
  LCLinkResolverLink,
}: {
  instance: SearchInstanceDto;
  LCLinkResolverLink?: string;
}) => {
  const { $t } = useIntl();
  const { getInstanceDetailsRoutePath } = useInstanceDetailsRoutePath();
  const childInstances = useInstanceRelationships(instance?.childInstances);

  const onlineAccessMetadata = useMemo(
    () =>
      formatInstanceMetadata(instance, [
        {
          title: $t({
            id: 'instanceDetails.onlineLinks',
            defaultMessage: 'Online links',
          }),
          getData: instance => instance.electronicAccess,
          formatter: formatElectronicAccess,
        },
        {
          title: $t({
            id: 'instanceDetails.LCLink.title',
            defaultMessage: 'Find it',
          }),
          getData: () => LCLinkResolverLink,
          formatter: LCLink =>
            getExternalLink(
              $t({
                id: 'instanceDetails.LCLink',
                defaultMessage: 'Full text finder',
              }),
              LCLink || ''
            ),
        },
      ]),
    [$t, LCLinkResolverLink, instance]
  );

  const titlesMetadata = useMemo(
    () =>
      formatInstanceMetadata(instance, [
        {
          title: $t(
            {
              id: 'instanceDetails.otherTitles',
              defaultMessage:
                '{itemCount, plural, one {Other title} other {Other titles}}',
            },
            { itemCount: size(instance?.alternativeTitles) }
          ),
          getData: instance =>
            instance.alternativeTitles?.map(
              ({ alternativeTitle }) => alternativeTitle
            ),
          formatter: renderMetadataList,
        },
      ]),
    [$t, instance]
  );

  const publicationDetailsMetadata = useMemo(
    () =>
      formatInstanceMetadata(instance, [
        {
          title: $t(sharedMessages.language),
          getData: instance => {
            const filteredLanguage = instance.languages?.flatMap(language =>
              langMessages[language] ? [$t(langMessages[language])] : []
            );

            return !isEmpty(filteredLanguage)
              ? filteredLanguage
              : [$t(langMessages.undefined)];
          },
          formatter: formatArrayToString,
        },
        {
          testId: 'publicationDetailsNotes',
          getData: instance =>
            getNotesData(instance, publicationDetailsNoteTypeMessages),
          formatter: data =>
            formatNotes(publicationDetailsNoteTypeMessages, data),
        },
        {
          title: $t(sharedMessages.publisher),
          getData: getPublishers,
          formatter: renderMetadataList,
        },
        {
          title: $t({
            id: 'instanceDetails.recordsInCollection',
            defaultMessage: 'Records in the collection',
          }),
          testId: 'childInstances',
          getData: () => childInstances,
          formatter: data => (
            <ShowMore limit={childInstancesLimit}>
              <ul>
                <ShowMore.Items>
                  {data.map(({ id: instanceId, title }) => (
                    <li key={instanceId}>
                      <Link
                        to={getInstanceDetailsRoutePath(instanceId)}
                        key={title}
                        className="underlinedLink"
                      >
                        {title}
                      </Link>
                    </li>
                  ))}
                </ShowMore.Items>
              </ul>
              <ShowMoreButton
                showMoreLimit={childInstancesLimit}
                dataLength={data.length}
              />
            </ShowMore>
          ),
        },
        {
          title: $t({
            id: 'instanceDetails.precedingTitle',
            defaultMessage: 'Preceding title',
          }),
          getData: instance => instance.precedingTitles,
          formatter: formatSiblingInstancesList,
        },
        {
          title: $t({
            id: 'instanceDetails.succeedingTitle',
            defaultMessage: 'Succeeding title',
          }),
          getData: instance => instance.succeedingTitles,
          formatter: formatSiblingInstancesList,
        },
        {
          title: $t(sharedMessages.series),
          getData: instance => instance.series?.map(({ value }) => value),
          formatter: renderMetadataList,
        },
        {
          title: $t({
            id: 'instanceDetails.documentType',
            defaultMessage: 'Document type',
          }),
          getData: instance => instance.sourceTypes,
          formatter: renderMetadataList,
        },
        {
          title: $t({
            id: 'instanceDetails.physicalDescription',
            defaultMessage: 'Physical description',
          }),
          getData: instance => instance.physicalDescriptions,
          formatter: formatArrayToString,
        },
        {
          title: $t({
            id: 'instanceDetails.source',
            defaultMessage: 'Source',
          }),
          getData: instance => instance.source,
        },
      ]),
    [$t, childInstances, getInstanceDetailsRoutePath, instance]
  );

  const contentsSummaryMetadata = useMemo(
    () =>
      formatInstanceMetadata(instance, [
        {
          testId: 'contentsSummaryNotes',
          getData: instance =>
            getNotesData(instance, contentsSummaryNoteTypeMessages),
          formatter: data => formatNotes(contentsSummaryNoteTypeMessages, data),
        },
      ]),
    [instance]
  );

  const additionalInformationMetadata = useMemo(
    () =>
      formatInstanceMetadata(instance, [
        {
          testId: 'additionalInformationNotes',
          getData: instance =>
            getNotesData(instance, additionalInformationNoteTypeMessages),
          formatter: data =>
            formatNotes(additionalInformationNoteTypeMessages, data),
        },
      ]),
    [instance]
  );

  const identifiersMetadata = useMemo(
    () =>
      formatInstanceMetadata(instance, [
        {
          title: identifierType.isbn,
          getData: instance => instance.isbns,
          formatter: renderMetadataList,
        },
        {
          title: identifierType.issn,
          getData: instance => instance.issns,
          formatter: renderMetadataList,
        },
        {
          title: identifierType.lccn,
          getData: instance => instance.lccns,
          formatter: renderMetadataList,
        },
        {
          title: identifierType.oclc,
          getData: instance => instance.oclcs,
          formatter: renderMetadataList,
        },
        {
          title: $t({
            id: 'instanceDetails.classificationLC',
            defaultMessage: 'Classification - LC',
          }),
          getData: instance =>
            formatClassifications(
              ClassificationType.lc,
              instance?.classifications
            ),
          formatter: renderMetadataList,
        },
        {
          title: $t({
            id: 'instanceDetails.classificationDewey',
            defaultMessage: 'Classification - Dewey',
          }),
          getData: instance =>
            formatClassifications(
              ClassificationType.dewey,
              instance?.classifications
            ),
          formatter: renderMetadataList,
        },
      ]),
    [$t, instance]
  );

  return useMemo(
    () => [
      {
        title: $t({
          id: 'instanceDetails.onlineAccess',
          defaultMessage: 'Online access',
        }),
        items: onlineAccessMetadata,
      },
      {
        title: $t({ id: 'instanceDetails.titles', defaultMessage: 'Title/s' }),
        items: titlesMetadata,
      },
      {
        title: $t({
          id: 'instanceDetails.publicationDetails',
          defaultMessage: 'Publication details',
        }),
        items: publicationDetailsMetadata,
      },
      {
        title: $t({
          id: 'instanceDetails.contentsSummary ',
          defaultMessage: 'Contents/Summary',
        }),
        items: contentsSummaryMetadata,
      },
      {
        title: $t({
          id: 'instanceDetails.additionalInformation ',
          defaultMessage: 'Additional information',
        }),
        items: additionalInformationMetadata,
      },
      {
        title: $t({
          id: 'instanceDetails.identifiersAndNumbering',
          defaultMessage: 'Identifiers and numbering',
        }),
        items: identifiersMetadata,
      },
    ],
    [
      $t,
      additionalInformationMetadata,
      contentsSummaryMetadata,
      identifiersMetadata,
      onlineAccessMetadata,
      publicationDetailsMetadata,
      titlesMetadata,
    ]
  );
};
